/**
 * rPI Porter — Partner Timeline widget (E-102 P3-3, ADR-102 v0.3 §6)
 *
 * Route: /porter/inbox/partners/:id/timeline
 *
 * Lists all CustomerThreads for a given partnerId in chronological order
 * (newest first). Each thread card shows: status badge, channel icon,
 * last-message preview, lastMessageAt. Click → navigate to thread detail.
 *
 * Backend: uses existing GET /api/v1/threads?partnerId={id} (P1-4).
 * No new backend endpoint needed.
 *
 * SCOPE LOCK: does NOT modify InboxListPage or InboxThreadDetailPage.
 */

import type { ThreadChannel, ThreadStatus, ThreadSummary } from '@/api/porter-threads';
import { listThreads } from '@/api/porter-threads';
import { useTranslation } from '@kgc/i18n';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';

// ============================================
// Constants
// ============================================

const TEAL = '#14B8A6';

const CHANNEL_ICON: Record<ThreadChannel, string> = {
  EMAIL: '📧',
  WEBCHAT: '💬',
  CONTACT_FORM: '📝',
};

const STATUS_COLOR: Record<ThreadStatus, string> = {
  OPEN: TEAL,
  PENDING: '#f59e0b',
  RESOLVED: '#64748b',
  SNOOZED: '#8b5cf6',
};

// ============================================
// Loading skeleton
// ============================================

function LoadingSkeleton() {
  return (
    <div data-testid="timeline-loading" style={{ padding: '24px' }}>
      {[1, 2, 3, 4].map(i => (
        <div
          key={i}
          style={{
            height: 72,
            background: '#1e293b',
            borderRadius: 8,
            marginBottom: 10,
            opacity: 0.7 - i * 0.1,
          }}
        />
      ))}
    </div>
  );
}

// ============================================
// Thread card
// ============================================

interface ThreadCardProps {
  thread: ThreadSummary;
  onClick: () => void;
  t: (key: string) => string;
}

function ThreadCard({ thread, onClick, t }: ThreadCardProps) {
  const icon = CHANNEL_ICON[thread.channel] ?? '💬';
  const statusColor = STATUS_COLOR[thread.status] ?? '#64748b';

  // Format lastMessageAt relative time
  const lastMsgDate = new Date(thread.lastMessageAt);
  const formattedDate = Number.isNaN(lastMsgDate.getTime())
    ? '—'
    : lastMsgDate.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });

  return (
    <button
      data-testid={`timeline-thread-${thread.id}`}
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        width: '100%',
        background: '#1e293b',
        border: '1px solid #334155',
        borderRadius: 8,
        padding: '14px 18px',
        marginBottom: 10,
        cursor: 'pointer',
        textAlign: 'left',
        transition: 'border-color 0.15s',
      }}
      onMouseEnter={e => {
        (e.currentTarget as HTMLButtonElement).style.borderColor = TEAL;
      }}
      onMouseLeave={e => {
        (e.currentTarget as HTMLButtonElement).style.borderColor = '#334155';
      }}
    >
      {/* Channel icon */}
      <span style={{ fontSize: 20, flexShrink: 0 }}>{icon}</span>

      {/* Thread info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            color: '#f8fafc',
            fontSize: 14,
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {thread.subject ??
            thread.customerName ??
            thread.customerEmail ??
            t('porter.inbox.noSubject')}
        </div>
        <div style={{ color: '#64748b', fontSize: 12, marginTop: 2 }}>
          {t('porter.timeline.lastMessage')}: {formattedDate}
        </div>
      </div>

      {/* Status badge */}
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: statusColor,
          background: `${statusColor}1a`,
          padding: '2px 8px',
          borderRadius: 10,
          flexShrink: 0,
        }}
      >
        {thread.status}
      </div>

      {/* Arrow */}
      <span style={{ color: '#64748b', fontSize: 16, flexShrink: 0 }}>›</span>
    </button>
  );
}

// ============================================
// Main page
// ============================================

export function PartnerTimelinePage() {
  const { t } = useTranslation();
  const { id: partnerId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['porter', 'threads', 'partner', partnerId] as const,
    queryFn: () => listThreads({ partnerId, limit: 100 }),
    enabled: partnerId !== undefined,
    staleTime: 30_000,
  });

  const threads = data?.data ?? [];

  function handleThreadClick(threadId: string) {
    void navigate(`/porter/inbox/${threadId}`);
  }

  if (isLoading) return <LoadingSkeleton />;

  if (isError) {
    return (
      <div data-testid="timeline-error" style={{ padding: 32, color: '#ef4444' }}>
        {t('porter.timeline.error')}
      </div>
    );
  }

  return (
    <div
      data-testid="partner-timeline-page"
      style={{ maxWidth: 720, margin: '0 auto', padding: '32px 24px' }}
    >
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ color: '#f8fafc', fontSize: 20, fontWeight: 700, marginBottom: 4 }}>
          {t('porter.timeline.title')}
        </h2>
        <p style={{ color: '#94a3b8', fontSize: 14, margin: 0 }}>{t('porter.timeline.subtitle')}</p>
      </div>

      {/* Thread list */}
      {threads.length === 0 ? (
        <div
          data-testid="timeline-empty"
          style={{
            background: '#1e293b',
            borderRadius: 8,
            padding: 32,
            textAlign: 'center',
            color: '#64748b',
          }}
        >
          {t('porter.timeline.empty')}
        </div>
      ) : (
        <div data-testid="timeline-list">
          {threads.map(thread => (
            <ThreadCard
              key={thread.id}
              thread={thread}
              onClick={() => {
                handleThreadClick(thread.id);
              }}
              t={t}
            />
          ))}
        </div>
      )}
    </div>
  );
}
